import React, { useCallback, useMemo } from "react";
import {
  Box,
  Paper,
  Switch,
  Typography,
  InputAdornment,
  Radio,
  RadioGroup,
  FormControlLabel,
  Avatar,
  Stack,
  Divider,
} from "@mui/material";
import {
  Height as HeightIcon,
  Scale,
  WaterDrop,
} from "@mui/icons-material";
import CustomInput from "../../../../FormFields/CustomInput/CustomInput";
import CustomSelect from "../../../../FormFields/CustomSelect/CustomSelect";

const VitalParameters = ({ formData, setFormData }) => {
  // Use useCallback to prevent unnecessary re-renders
  const handleVitalChange = useCallback((name, field, val) => {
    setFormData((prev) => {
      const list = [...(prev.vital_targets || [])];
      const idx = list.findIndex((v) => v.name === name);
      if (idx > -1) {
        list[idx] = { ...list[idx], [field]: val };
      } else {
        list.push({ name, monitor: true, [field]: val });
      }
      return { ...prev, vital_targets: list };
    });
  }, [setFormData]);

  // Memoize getVal to prevent re-creation
  const getVal = useCallback((name, field) => {
    const row = formData.vital_targets?.find((t) => t.name === name);
    return row?.[field] ?? "";
  }, [formData.vital_targets]);

  const getStatus = useCallback((name) => {
    const row = formData.vital_targets?.find((t) => t.name === name);
    return row?.monitor ?? true;
  }, [formData.vital_targets]);

  const VitalCard = useCallback(({ title, active, onToggle, children }) => (
    <Paper
      elevation={0}
      sx={{
        p: 1.25,
        borderRadius: "8px",
        border: "1px solid",
        borderColor: active ? "#E2E8F0" : "#F1F5F9",
        bgcolor: active ? "#FFFFFF" : "#F8FAFC",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: active ? "#6366F1" : "#E2E8F0",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 110 }}>
          <Switch
            checked={active}
            onChange={(e) => onToggle(e.target.checked)}
            size="small"
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: "#EF4444" },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                bgcolor: "#EF4444",
              },
            }}
          />
          <Typography variant="body2" fontWeight={600} color={active ? "#1E293B" : "#94A3B8"} fontSize="0.75rem">
            {title}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, opacity: active ? 1 : 0.4, pointerEvents: active ? "auto" : "none" }}>
          {children}
        </Box>
      </Box>
    </Paper>
  ), []);

  // Memoize the vital cards to prevent re-renders
  const vitalCards = useMemo(() => {
    const items = [
      { name: "Blood Sugar", unit: "mmols", placeholder: "BM" },
      { name: "Respiration Rate", unit: "rpm", placeholder: "Value" },
      { name: "Pulse Rate", unit: "bpm", placeholder: "Value" },
      { name: "Temperature", unit: "°C", placeholder: "Value" },
    ];

    return items.map((item) => (
      <Box key={item.name} sx={{ flex: "1 1 calc(50% - 8px)", minWidth: 200 }}>
        <VitalCard
          title={item.name}
          active={getStatus(item.name)}
          onToggle={(val) => handleVitalChange(item.name, "monitor", val)}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <CustomInput
              size="small"
              placeholder={item.placeholder}
              value={getVal(item.name, "min")}
              onChange={(e) => handleVitalChange(item.name, "min", e.target.value)}
              sx={{ width: 70 }}
            />
            <Typography fontSize="0.6rem" fontWeight={600} color="#94A3B8">
              To
            </Typography>
            <CustomInput
              size="small"
              placeholder={item.placeholder}
              value={getVal(item.name, "max")}
              onChange={(e) => handleVitalChange(item.name, "max", e.target.value)}
              sx={{ width: 70 }}
            />
            <Typography variant="caption" color="#94A3B8" fontWeight={500} sx={{ minWidth: 24, fontSize: "0.65rem" }}>
              {item.unit}
            </Typography>
          </Box>
        </VitalCard>
      </Box>
    ));
  }, [VitalCard, getStatus, handleVitalChange, getVal]);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} color="#0F172A" fontSize="0.8rem">
          Usual Parameters
        </Typography>
        <Typography variant="caption" color="#64748B" fontSize="0.65rem">
          These parameters should be implemented by the Community Matron/ANP/GP or those qualified to do so
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: 1.25,
            borderRadius: "8px",
            bgcolor: "#F8FAFC",
            border: "1px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 0.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <HeightIcon sx={{ color: "#64748B", fontSize: 18 }} />
            <Typography variant="body2" fontWeight={600} color="#1E293B" fontSize="0.8rem">
              Height
            </Typography>
          </Box>
          <CustomInput
            placeholder="Enter height"
            value={getVal("Height", "value")}
            onChange={(e) => handleVitalChange("Height", "value", e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ "& .MuiTypography-root": { fontSize: "0.7rem" } }}>
                  metres
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 160 }, bgcolor: "white" }}
            size="small"
          />
        </Paper>

        <VitalCard
          title="Blood Pressure"
          active={getStatus("Blood Pressure")}
          onToggle={(val) => handleVitalChange("Blood Pressure", "monitor", val)}
        >
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
            <CustomInput
              size="small"
              placeholder="Sys min"
              value={getVal("Blood Pressure", "sys_min")}
              onChange={(e) => handleVitalChange("Blood Pressure", "sys_min", e.target.value)}
              sx={{ width: 70 }}
            />
            <Typography fontSize="0.75rem" color="#94A3B8">
              /
            </Typography>
            <CustomInput
              size="small"
              placeholder="Dia min"
              value={getVal("Blood Pressure", "dia_min")}
              onChange={(e) => handleVitalChange("Blood Pressure", "dia_min", e.target.value)}
              sx={{ width: 70 }}
            />
            <Typography fontSize="0.6rem" fontWeight={600} color="#94A3B8" sx={{ px: 0.5 }}>
              To
            </Typography>
            <CustomInput
              size="small"
              placeholder="Sys max"
              value={getVal("Blood Pressure", "sys_max")}
              onChange={(e) => handleVitalChange("Blood Pressure", "sys_max", e.target.value)}
              sx={{ width: 70 }}
            />
            <Typography fontSize="0.75rem" color="#94A3B8">
              /
            </Typography>
            <CustomInput
              size="small"
              placeholder="Dia max"
              value={getVal("Blood Pressure", "dia_max")}
              onChange={(e) => handleVitalChange("Blood Pressure", "dia_max", e.target.value)}
              sx={{ width: 70 }}
            />
            <Typography variant="caption" color="#94A3B8" fontWeight={500} sx={{ ml: 0.5, fontSize: "0.6rem" }}>
              mmHg
            </Typography>
          </Box>
        </VitalCard>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {vitalCards}
        </Box>

        <VitalCard
          title="Oxygen Saturation"
          active={getStatus("Oxygen Saturation")}
          onToggle={(val) => handleVitalChange("Oxygen Saturation", "monitor", val)}
        >
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
            <CustomInput
              size="small"
              placeholder="Value"
              value={getVal("Oxygen Saturation", "min")}
              onChange={(e) => handleVitalChange("Oxygen Saturation", "min", e.target.value)}
              sx={{ width: 70 }}
            />
            <Typography fontSize="0.6rem" fontWeight={600} color="#94A3B8">
              To
            </Typography>
            <CustomInput
              size="small"
              placeholder="Value"
              value={getVal("Oxygen Saturation", "max")}
              onChange={(e) => handleVitalChange("Oxygen Saturation", "max", e.target.value)}
              sx={{ width: 70 }}
            />
            <Typography variant="caption" color="#94A3B8" fontWeight={500} sx={{ minWidth: 20, fontSize: "0.65rem" }}>
              %
            </Typography>
            <RadioGroup
              row
              value={getVal("Oxygen Saturation", "scale") || "scale1"}
              onChange={(e) => handleVitalChange("Oxygen Saturation", "scale", e.target.value)}
              sx={{ ml: 0.5 }}
            >
              <FormControlLabel
                value="scale1"
                control={<Radio size="small" />}
                label={<Typography variant="caption" fontSize="0.6rem">Scale 1</Typography>}
              />
              <FormControlLabel
                value="scale2"
                control={<Radio size="small" />}
                label={<Typography variant="caption" fontSize="0.6rem">Scale 2</Typography>}
              />
            </RadioGroup>
          </Box>
        </VitalCard>

        <VitalCard
          title="Respiratory Condition"
          active={getStatus("Respiratory Condition")}
          onToggle={(val) => handleVitalChange("Respiratory Condition", "monitor", val)}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <CustomSelect
              label="Clinical Diagnosis"
              value={getVal("Respiratory Condition", "condition")}
              onChange={(e) => handleVitalChange("Respiratory Condition", "condition", e.target.value)}
              options={[
                { label: "Normal / None", value: "Normal" },
                { label: "COPD", value: "COPD" },
                { label: "Asthma", value: "Asthma" },
                { label: "Emphysema", value: "Emphysema" },
                { label: "Chronic Bronchitis", value: "Chronic Bronchitis" },
                { label: "Bronchiectasis", value: "Bronchiectasis" },
                { label: "Cystic Fibrosis", value: "Cystic Fibrosis" },
                { label: "Pulmonary Fibrosis", value: "Pulmonary Fibrosis" },
                { label: "Pulmonary Hypertension", value: "Pulmonary Hypertension" },
                { label: "Sleep Apnoea (OSA)", value: "Sleep Apnoea" },
                { label: "Sarcoidosis", value: "Sarcoidosis" },
                { label: "Pneumonia", value: "Pneumonia" },
                { label: "Tuberculosis", value: "Tuberculosis" },
                { label: "Post-COVID Respiratory Syndrome", value: "Long COVID" },
                { label: "Other", value: "Other" },
              ]}
              size="small"
              sx={{ "& .MuiInputLabel-root": { fontSize: "0.7rem" } }}
            />
            {getVal("Respiratory Condition", "condition") === "Other" && (
              <CustomInput
                placeholder="Enter specific respiratory cause..."
                value={getVal("Respiratory Condition", "custom_cause")}
                onChange={(e) => handleVitalChange("Respiratory Condition", "custom_cause", e.target.value)}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    bgcolor: "#FFFBEB",
                    border: "1px solid #FCD34D",
                  },
                }}
              />
            )}
          </Box>
        </VitalCard>
      </Box>

      <Divider sx={{ my: 2.5 }} />

      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: "#475569",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          fontSize: "0.6rem",
          mb: 1.5,
          display: "block",
        }}
      >
        Alerts & Targets
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
        <Box sx={{ flex: "1 1 calc(50% - 12px)", minWidth: 240 }}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: "8px",
              border: "2px dashed #E2E8F0",
              bgcolor: "#FCFDFE",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Avatar sx={{ bgcolor: "#FFF7ED", color: "#F97316", width: 28, height: 28 }}>
                <Scale sx={{ fontSize: 14 }} />
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight={700} color="#1E293B" fontSize="0.75rem">
                  Weight Change Alert
                </Typography>
                <Typography variant="caption" color="#94A3B8" fontSize="0.6rem">
                  Trigger on KG change
                </Typography>
              </Box>
            </Stack>
            <CustomInput
              placeholder="0.00"
              value={formData.weight_change_alert_kg ?? ""}
              onChange={(e) => setFormData({ ...formData, weight_change_alert_kg: e.target.value })}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ "& .MuiTypography-root": { fontSize: "0.7rem" } }}>
                    kg
                  </InputAdornment>
                ),
                sx: { borderRadius: "8px", bgcolor: "white" },
              }}
            />
          </Paper>
        </Box>

        <Box sx={{ flex: "1 1 calc(50% - 12px)", minWidth: 240 }}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: "8px",
              bgcolor: "#EFF6FF",
              border: "1px solid #DBEAFE",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar sx={{ bgcolor: "#3B82F6", color: "white", width: 28, height: 28 }}>
                  <WaterDrop sx={{ fontSize: 14 }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={700} color="#1E3A8A" fontSize="0.75rem">
                    Hydration Target
                  </Typography>
                  <Typography variant="caption" color="#60A5FA" fontSize="0.6rem">
                    Daily fluid goal
                  </Typography>
                </Box>
              </Stack>
              <Switch
                checked={formData.fluid_target_required ?? false}
                onChange={(e) => setFormData({ ...formData, fluid_target_required: e.target.checked })}
                size="small"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#3B82F6" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    bgcolor: "#3B82F6",
                  },
                }}
              />
            </Stack>
            {formData.fluid_target_required && (
              <CustomInput
                placeholder="2000"
                value={formData.fluid_target_ml ?? ""}
                onChange={(e) => setFormData({ ...formData, fluid_target_ml: e.target.value })}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ "& .MuiTypography-root": { fontSize: "0.7rem" } }}>
                      ml
                    </InputAdornment>
                  ),
                  sx: { borderRadius: "8px", bgcolor: "white" },
                }}
              />
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default VitalParameters;